import { ConfirmationDialogRaw } from "@/controls/modals/ConfirmationDialogRaw";
import { ActiveCarrierModal } from "@/pages/catalogs/shared/components/ActiveCarrierModal";
import { RollsetDeactivationConfirmModal } from "@/pages/catalogs/shared/components/RollsetDeactivationConfirmModal";
import { upsertRoll } from "@/pages/rollshop/repositories/OperationRepository";
import { Roll } from "@/shared/models/Roll";
import { useRolls } from "@/shared/selectors/roll/RollSelector";
import { RollSelectorDialog } from "@/shared/selectors/roll/RollSelectorDialog";
import { ErrorModal } from "@dexteel/mesf-core";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

type Props = {
	open: boolean;
	onClose: () => void;
};

export const AddBrokenRollDialog = ({ open, onClose }: Props) => {
	const queryClient = useQueryClient();

	const [error, setError] = useState("");
	const [pendingRoll, setPendingRoll] = useState<Roll | null>(null);

	const { data: rolls, isLoading } = useRolls({
		onError: (e) => setError(e.message),
		enabled: open,
	});

	const upsertMutation = useMutation({
		mutationFn: (partId: number) => upsertRoll({ partId, statusCode: "K" }),
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: ["brokenRolls"] });
		},
		onError: (e: Error) => setError(e.message),
	});

	const willDeactivateRollset =
		pendingRoll != null && pendingRoll.RollsetIsActive;
	const notEditable = pendingRoll != null && pendingRoll.IsInActiveCarrier;

	const confirmUpsert = () => {
		const roll = pendingRoll;
		setPendingRoll(null);
		if (roll) upsertMutation.mutate(roll.PartId);
	};

	const cancelUpsert = () => setPendingRoll(null);

	return (
		<>
			<RollSelectorDialog
				open={open}
				onClose={onClose}
				onSelect={(roll) => {
					if (roll) setPendingRoll(roll);
				}}
				rolls={rolls ?? []}
				isLoading={isLoading}
				title="MARK ROLL AS BROKEN"
			/>
			<ConfirmationDialogRaw
				title="CONFIRMATION"
				message={
					pendingRoll
						? `Are you sure you want to mark "${pendingRoll.PartName}" as broken?`
						: ""
				}
				open={!!pendingRoll && !willDeactivateRollset}
				onClose={(value) => {
					if (value === "OK") confirmUpsert();
					else cancelUpsert();
				}}
			/>
			<RollsetDeactivationConfirmModal
				show={willDeactivateRollset}
				entityName={pendingRoll?.PartName ?? ""}
				entityType="Roll"
				actionDescription="Marking this roll as broken"
				rollsetInfo={{
					RollsetId: pendingRoll?.RollsetId ?? null,
					RollsetLetter: pendingRoll?.RollsetLetter ?? null,
					RollsetSectionName: pendingRoll?.RollsetSectionName ?? null,
					RollsetStandName: pendingRoll?.RollsetStandName ?? null,
					RollsetTopRollName: pendingRoll?.RollsetTopRollName ?? null,
					RollsetBottomRollName: pendingRoll?.RollsetBottomRollName ?? null,
				}}
				onConfirm={confirmUpsert}
				onCancel={cancelUpsert}
			/>
			<ActiveCarrierModal
				show={notEditable}
				entityName={pendingRoll?.PartName ?? ""}
				entityType="Roll"
				actionDescription={`Cant change the status of this roll because it is in an active carrier.`}
				action="status"
				carrier={{
					CarrierName: pendingRoll?.CarrierName ?? "",
					CarrierStatusName: pendingRoll?.CarrierStatusName ?? "",
				}}
				rollsetInfo={{
					RollsetId: pendingRoll?.RollsetId ?? null,
					RollsetLetter: pendingRoll?.RollsetLetter ?? null,
					RollsetSectionName: pendingRoll?.RollsetSectionName ?? null,
					RollsetStandName: pendingRoll?.RollsetStandName ?? null,
					RollsetTopRollName: pendingRoll?.RollsetTopRollName ?? null,
					RollsetBottomRollName: pendingRoll?.RollsetBottomRollName ?? null,
				}}
				onClose={cancelUpsert}
			/>
			<ErrorModal
				error={error}
				onHide={() => setError("")}
				title="Error marking roll as broken"
			/>
		</>
	);
};
